<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-auto-qa gauge-panel">
    <div class="panel-section" v-loading="controller.loading">
      <div class="panel-column gauge" :class="({'loading':controller.loading})" >
        <span class="panel-title">Average auto evaluation score</span>
        <div ref="chart0"></div>
        <div v-if="this.gaugeData && this.gaugeData.avgAutoEvalScore" class="panel-info">
          <span class="panel-gauge-name">AES</span>
          <div class="min-max-label">
            <span style="margin-right: 33px">0</span>
            <span style="margin-right: -9px">100</span>
          </div>
          <div style="display: flex; align-items: center; gap: 8px">
            <span class="panel-reference" :style="{ color: getColorValue(this.gaugeData.avgAutoEvalScore.value, 'bes') }">{{ formatPanelValue({ value: this.gaugeData.avgAutoEvalScore.value }) }}</span>
          </div>
          <span class="panel-target">
          </span>
        </div>
      </div>
     

      <div class="panel-column total-overview" ref="volume">
        <div class="panel-row" :class="({'loading':controller.loading})">
          <div class="panel-title">
            <span >Total auto evaluated contacts</span>
          </div>
          <div v-if="gaugeData&&gaugeData.totalAutoEvaluatedContacts" class="panel-value">
            {{ formatPanelValue({value: gaugeData.totalAutoEvaluatedContacts.value}) }}
          </div>
        </div>
        <div class="panel-row" :class="({'loading':controller.loading})">
          <div class="panel-title">
            <span class="panel-title">Total auto evaluated agents</span>
          </div>
          <div v-if="gaugeData&&gaugeData.totalAutoEvaluatedAgents" class="panel-value">
            {{ formatPanelValue({value: gaugeData.totalAutoEvaluatedAgents.value}) }}
          </div>
        </div>
      </div> 
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { AvailableGaugePanels, CONFIG_BENCHMARK_MAPPER, CONFIG_KEY_MAPPER } from './panel-chart-const';

export default Vue.extend({
  name: 'wy-home-auto-qa-panel-performance-gauge-summary',
  props: ['controller'],
  data() {
    return {
      gaugeData: {},
      chartIndex: 0,
      pieChartsLoading: false,
      volumeNumOfDaysInRange: null,
    };
  },

  computed: {
    ...mapGetters(['dataModel', 'companyProfileSettings', 'companyConfig']),

    configBesBasParent() {
      return (
        this.companyProfileSettings &&
        this.companyProfileSettings.targetSettings &&
        this.companyProfileSettings.targetSettings.settings &&
        this.companyProfileSettings.targetSettings.settings &&
        this.companyProfileSettings.targetSettings.settings.home &&
        this.companyProfileSettings.targetSettings.settings.home.experienceAutomationSummary &&
        this.companyProfileSettings.targetSettings.settings.home.experienceAutomationSummary.default
      );
    },
    isConfigBesBasActive() {
      return this.configBesBasParent && this.configBesBasParent.actived;
    },
    configBesBasValues() {
      return this.isConfigBesBasActive && this.configBesBasParent && this.configBesBasParent.target;
    },

    showAcPanel() {
      return this.companyConfig && this.companyConfig.api && this.companyConfig.api.configs && this.companyConfig.api.configs.acConfig;
    },

    // TARGETS
    showBasMaxValue() {
      return this.computeShowTarget({ field: 'maxValue', panel: 'bas' });
    },
    showBasDefaultValue() {
      return this.computeShowTarget({ field: 'value', panel: 'bas' });
    },
    showAcMinValue() {
      return this.computeShowTarget({ field: 'minValue', panel: 'ac' });
    },
    showAcDefaultValue() {
      return this.computeShowTarget({ field: 'value', panel: 'ac' });
    },
    showBesMaxValue() {
      return this.computeShowTarget({ field: 'maxValue', panel: 'bes' });
    },
    showBesDefaultValue() {
      return this.computeShowTarget({ field: 'value', panel: 'bes' });
    },
  },

  methods: {
    ...mapActions(['fetchAutoQASummaryPanel', 'fetchAutoQALineChart', 'fetchAutoQABarChart']),

    computeShowTarget({ field, panel }) {
      const baseObjectExists = this.configBesBasValues && this.configBesBasValues[panel];

      const maxValueIsNotNull = baseObjectExists && this.configBesBasValues[panel][field] != null;
      const maxValueIsNotUndefined = baseObjectExists && this.configBesBasValues[panel][field] != undefined;

      const maxValueIsNumber = baseObjectExists && typeof this.configBesBasValues[panel][field] === 'number';
      const maxValueIsString = baseObjectExists && typeof this.configBesBasValues[panel][field] === 'string';

      const maxValueIsPositive = baseObjectExists && this.configBesBasValues[panel][field] >= 0;
      const maxValueIsNotEmptyString = baseObjectExists && this.configBesBasValues[panel][field] != '';

      const baseCondition = baseObjectExists && maxValueIsNotNull && maxValueIsNotUndefined;

      if (maxValueIsNumber) {
        return baseCondition && maxValueIsPositive;
      }
      if (maxValueIsString) {
        return baseCondition && maxValueIsNotEmptyString;
      }
      return false;
    },

    formatPanelValue({ value, prefix, postfix, type }) {
      if (value == null || value == '-1') return 'N/A';
      value = value.toString().replace(/^-/, '');

      // if (this.selectedGaugePanel === AvailableGaugePanels.automated_chat) {
      //   return `${prefix || ''}${value && value.toFixed(2)}${postfix || ''}`;
      // }
      if(type && type == 'ac') {
        return `${prefix || ''}${value && Number(value).toFixed(2)}${postfix || ''}`;
      }
      else {
        return `${prefix || ''}${value}${postfix || ''}`;
      }
    },

    formatVolume(value) {
      if (!value || value == '-1') return 'N/A';
      // remove + or - in the beginning of the value
      value = value.toString().replace(/^[+-]/, '');

      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M';
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'k';
      } else {
        return value;
      }
    },

    getVolumeArrow(value) {
      if (!value || value == '-1') return '';
      if (value > 0) return 'volume-up-icon';
      return 'volume-down-icon';
    },

    getVolumePill(value) {
      if (!value || value == '-1') return 'none';
      if (value > 0) return 'up';
      return 'down';
    },

    getColorValue(value, panel) {
      let target = this.configBesBasValues && this.configBesBasValues[panel];
      // for ac, we use the lower limit for target
      if(panel === 'ac') {
        if (target && target.minValue != null && target.minValue >= 0) {
          target = target.minValue;
        } else {
          target = target && target.value;
        }
      }
      else {
        if (target && target.maxValue != null && target.maxValue >= 0) {
          target = target.maxValue;
        } else {
          target = target && target.value;
        }
      }

      if (!value || value == '-1' || target == null || target == undefined) return '#4B5158'; // Gray

      if (panel !== 'ac' && value > target) return '#186731'; // Green
      if (panel === 'ac' && value < target) return '#186731'; // Green

      return '#C11149'; // Red
    },

    async handlePanelChange(key, firstTime = false) {
      // this.controller.selectedGaugePanel = key;
      if (!firstTime) {
        const eventInfo = {
          metric_name: this.controller.metricsMap[key] ? this.controller.metricsMap[key] : key,
        };
        this.$router.logAmplitudeEvent({ eventName: 'Home_EA_EA: Metric Clicked', eventInfo });
      }

      const dynamicChartRef = this.controller.$refs.dynamicChart;
      if (!dynamicChartRef) return;
      const volumeApiAlreadyFetched = dynamicChartRef.networkData[key] && dynamicChartRef.networkData[key] && dynamicChartRef.networkData[key].dailyMetrics.length;
      // console.log(key, dynamicChartRef.networkData[key], volumeApiAlreadyFetched, !firstTime, dynamicChartRef.networkData)

      if (volumeApiAlreadyFetched && !firstTime) {
        dynamicChartRef.reloadBothPanels();
        this.handleRenderBarChart(dynamicChartRef, key);
        return;
      }

      this.controller.dynamicChartsLoading = true;

      const volumeData = await this.fetchAutoQALineChart();

      const panelDataExists = dynamicChartRef.networkData.autoEvalScore;
      const apiVolumeData = volumeData.autoEvalScore
      if (!panelDataExists && apiVolumeData) {
        dynamicChartRef.showLineChart = true;
        dynamicChartRef.networkData.autoEvalScore = apiVolumeData;

        const noData =
          dynamicChartRef.networkData.autoEvalScore.dailyMetrics.length &&
          dynamicChartRef.networkData.autoEvalScore.dailyMetrics.every((metric) => metric.value == -1);

        if (noData) {
          dynamicChartRef.showLineChart = false;
        }
      }
      else {
        dynamicChartRef.showLineChart = false;
      }
      const barData = await this.fetchAutoQABarChart();
      if(barData && barData.autoEvalSectionScore) {
        dynamicChartRef.barChartHasData = true;
        const barDataExists = barData && Object.keys(barData) && Object.keys(barData).length;

        if (!barDataExists) {
          dynamicChartRef.barChartHasData = false;
        }

        if(!dynamicChartRef.networkData.autoEvalScore) {
          dynamicChartRef.networkData.autoEvalScore = {}
        }

        dynamicChartRef.networkData.autoEvalScore.barMetrics = {};
        
        if(Object.keys(barData.autoEvalSectionScore.barMetrics).length > 0) {
          Object.keys(barData.autoEvalSectionScore.barMetrics).forEach((item) => {
            if(barData.autoEvalSectionScore.barMetrics[item] != null && barData.autoEvalSectionScore.barMetrics[item] != '-1') {
              dynamicChartRef.networkData.autoEvalScore.barMetrics[item] = barData.autoEvalSectionScore.barMetrics[item]
            } else {
              // for -1 case, we need to show "N/A" as the bar value, but "hide" the bar
              dynamicChartRef.networkData.autoEvalScore.barMetrics[item] = '0.00001'; // workaround to show N/A
            }
          })
        }
        const noData = Object.values(dynamicChartRef.networkData.autoEvalScore.barMetrics).every((metric) => metric == -1);
        if (noData) dynamicChartRef.barChartHasData = false;
      }
      dynamicChartRef.reloadBothPanels();
      this.handleRenderBarChart(dynamicChartRef, key);
    },

    handleRenderBarChart(dynamicChartRef) {
      dynamicChartRef.showBarChart = true;
    },

    renderChart() {
      if (this.gaugeData && Object.keys(this.gaugeData) && !Object.keys(this.gaugeData).length) return;

      // Order of dials panel is 1) BAS 2) AC 3) BES 4) Volume
      let orderedGaugeData = {};
      orderedGaugeData.avgAutoEvalScore = this.gaugeData.avgAutoEvalScore;
      for (const [key, perfData] of Object.entries(orderedGaugeData)) {

        const { value } = perfData;

        const benchmark = this.configBesBasValues && this.configBesBasValues[CONFIG_BENCHMARK_MAPPER[key]] && this.configBesBasValues[CONFIG_BENCHMARK_MAPPER[key]].value;
        const benchmarkExists = benchmark != null && benchmark >= 0;
        const tickValues = this.configBesBasValues && this.configBesBasValues[CONFIG_KEY_MAPPER[key]];

        // check for null, as user can define 0 as metric
        const defaultVal = tickValues && tickValues.value;
        const minVal = tickValues && tickValues.minValue;
        const maxVal = tickValues && tickValues.maxValue;

        const MIN = 0;
        const MAX_HUNDRED = 100;
        const MAX_TEN = 10;

        let ticks = [MIN, MIN];
        let visibleTicks = [MIN, MIN]; // we only show maxVal and hide minVal

        const existingBesBasConfig = tickValues && !tickValues.isNull && (defaultVal != null && defaultVal >= MIN);
        const newBesBasConfigFromUI = tickValues && !tickValues.isNull && (minVal != null && minVal >= MIN && maxVal != null && maxVal >= MIN);

        const acPanel = key === AvailableGaugePanels.automated_chat;
        const besOrBasPanel = key === AvailableGaugePanels.bes || key === AvailableGaugePanels.bas;

        // DEBUGGER
        // console.log('key', key, tickValues, minVal, maxVal, existingBesBasConfig, newBesBasConfigFromUI);

        if (existingBesBasConfig) {
          // BAS BES Existing Config
          ticks = [MIN, defaultVal, acPanel ? MAX_TEN : MAX_HUNDRED];
        } else if (newBesBasConfigFromUI) {
          // BAS BES Newly added config through UI
          ticks = [MIN, minVal, maxVal, acPanel ? MAX_TEN : MAX_HUNDRED];
        } else {
          // default value doesn't exist for this panel
          ticks = [MIN, acPanel ? MAX_TEN : MAX_HUNDRED];
        }
        visibleTicks = ticks;

        // remove the MIN and MAX from visible ticks since it's handled by html
        visibleTicks = visibleTicks.slice(1, visibleTicks.length - 1);

        if (visibleTicks.length === 1) {
          // If max value is equal to MAX_TEN || MAX_HUNDRED, we hide it - since html already shows
          const hideMaxTarget = maxVal == MAX_TEN || maxVal == MAX_HUNDRED || maxVal == MIN || maxVal == MIN;
          if (hideMaxTarget) visibleTicks = [];
        }

        // visibleTicks => [minValue, maxValue]. If difference is less than 4 (or 0.5 for AC), we don't render the minValue
        const minRequiredSpacing = 10;
        const minRequiredSpacingAc = 1;
        let difference = visibleTicks[visibleTicks.length - 1] - visibleTicks[0];

        if (
          !existingBesBasConfig &&
          ((acPanel && difference < minRequiredSpacingAc) ||
            (key === AvailableGaugePanels.bes && difference < minRequiredSpacing) ||
            (key === AvailableGaugePanels.bas && difference < minRequiredSpacing))
        ) {
          if(acPanel) {
            visibleTicks = visibleTicks.slice(0, 1);
          }
          else {
            visibleTicks = visibleTicks.slice(1, visibleTicks.length);
          }
        }

        const COLORS = Object.freeze({
          RED: '#D65858',
          YELLOW: '#F9DD81',
          GREEN: '#5FBBAA',
          GRAY: '#1B1F22',
        });

        let rangeColor = [COLORS.RED, COLORS.YELLOW, COLORS.GREEN];

        // WEIRD EDGE CASE where rangeColor[MIN] or [3] is shown
        if ((maxVal != MAX_HUNDRED && minVal == MIN) || (maxVal != MAX_TEN && minVal == MIN)) {
          visibleTicks = visibleTicks.slice(1, visibleTicks.length); // Remove the MIN
          rangeColor = [COLORS.YELLOW, COLORS.YELLOW, COLORS.GREEN];
          if (acPanel) rangeColor = [COLORS.YELLOW, COLORS.GREEN, COLORS.GREEN];
        }
        if ((maxVal == MAX_HUNDRED && minVal !== MIN) || (maxVal == MAX_TEN && minVal !== MIN)) {
          visibleTicks = visibleTicks.slice(0, visibleTicks.length - 1); // Remove the MAX
          rangeColor = [COLORS.RED, COLORS.YELLOW, COLORS.YELLOW];
          if (acPanel) rangeColor = [COLORS.RED, COLORS.RED, COLORS.YELLOW];
        }

        const minMaxCase = (maxVal == MAX_HUNDRED && minVal == MIN) || (maxVal == MAX_TEN && minVal == MIN);
        if (minMaxCase) {
          rangeColor = [COLORS.YELLOW];
          visibleTicks = [];
        }

        const bothHundredCase = (maxVal == MAX_HUNDRED && minVal == MAX_HUNDRED) || (maxVal == MAX_TEN && minVal == MAX_TEN); // also used in gaugeSteps else if statement
        if (bothHundredCase) {
          rangeColor = [COLORS.RED];
          visibleTicks = [];
        }

        const bothZeroCase = maxVal == MIN && minVal == MIN; // also used in gaugeSteps else if statement
        if (bothZeroCase) {
          rangeColor = [COLORS.GREEN];
          visibleTicks = [];
        }

        // If existing besbasconfig, we show red/green colors
        if (existingBesBasConfig && ticks.length === 3) {
          rangeColor = [COLORS.RED, COLORS.GREEN];
        }

        // If NA Case, or default value doesn't exist, there's only two ticks (MIN,MAX_HUNDRED) or (MAX_TEN,MIN)
        const naCase = ticks.length == 2; // also used in gaugeSteps if statement
        if (naCase) rangeColor = [COLORS.GRAY]; // gray

        // DEBUGGER
        // console.log('rangeColor', key, rangeColor, ticks);

        if (acPanel) {
          ticks = ticks.reverse();
          visibleTicks = visibleTicks.reverse();
          rangeColor = rangeColor.reverse();
        }

        let gaugeSteps = [];
        if (naCase) {
          gaugeSteps = [{ range: [ticks[0], ticks[1]], color: rangeColor[0], line: { width: 3, color: rangeColor[0] }, thickness: 0.95 }];
        } else if (ticks.length == 3 && rangeColor.length == 2) {
          gaugeSteps = [
            { range: [ticks[0], ticks[1]], color: rangeColor[acPanel ? 1 : 0], line: { width: 3, color: rangeColor[acPanel ? 1 : 0] }, thickness: 0.95 },
            { range: [ticks[1], ticks[2]], color: rangeColor[acPanel ? 0 : 1], line: { width: 3, color: rangeColor[acPanel ? 0 : 1] }, thickness: 0.95 },
          ];
        } else if (ticks.length == 4 && rangeColor.length == 3) {
          gaugeSteps = [
            { range: [ticks[0], ticks[1]], color: rangeColor[acPanel ? 2 : 0], line: { width: 3, color: rangeColor[acPanel ? 2 : 0] }, thickness: 0.95 },
            { range: [ticks[1], ticks[2]], color: rangeColor[acPanel ? 1 : 1], line: { width: 3, color: rangeColor[acPanel ? 1 : 1] }, thickness: 0.95 },
            { range: [ticks[2], ticks[3]], color: rangeColor[acPanel ? 0 : 2], line: { width: 3, color: rangeColor[acPanel ? 0 : 2] }, thickness: 0.95 },
          ];
        } else if ((minMaxCase || bothZeroCase || bothHundredCase) && ticks.length == 4 && rangeColor.length == 1) {
          gaugeSteps = [
            { range: [ticks[0], ticks[1]], color: rangeColor[0], line: { width: 3, color: rangeColor[0] }, thickness: 0.95 },
            { range: [ticks[1], ticks[2]], color: rangeColor[0], line: { width: 3, color: rangeColor[0] }, thickness: 0.95 },
            { range: [ticks[2], ticks[3]], color: rangeColor[0], line: { width: 3, color: rangeColor[0] }, thickness: 0.95 },
          ];
        }

        const data = [
          {
            value: value,
            type: 'indicator',
            mode: 'gauge',
            // delta: { reference: 39 }, // compare delta with value
            gauge: {
              ...(benchmarkExists && {
                threshold: {
                  line: { color: 'transparent', width: 2 },
                  thickness: 1,
                  value: benchmark,
                },
              }),
              borderwidth: 0, // outline width
              axis: {
                range: [ticks[0], ticks[ticks.length - 1]],
                tickvals: visibleTicks,
                ticklen: 0, // to hide visible ticks
                tickprefix: ' ', // controls padding away from bar
                ticksuffix: ' ',
                tickangle: 0,
                tickfont: {
                  family: 'Helvetica',
                  size: 13,
                  color: '#4B5158',
                },
                tickformat: key === AvailableGaugePanels.automated_chat ? '.2f' : '.0f',
              },
              bar: {
                thickness: 0, // bar width - set 1 to align outline
                color: 'transparent',
                line: {
                  color: 'black',
                  width: 0, // value thickness
                },
              },
              steps: gaugeSteps,
            },
          },
        ];

        // TODO: This has to be refactored when min max can be configurable
        const MAX_ANGLE_VALUE = 97;
        const MIN_ANGLE_VALUE = 3; // Below this value, the arrow points to negative (below the gauge chart)

        if (besOrBasPanel) {
          // Cases where value is greater than max value
          const betweenMaxAngleMaxHundred = data[0].value >= MAX_ANGLE_VALUE && data[0].value <= MAX_HUNDRED;
          const greaterThanMaxHundred = data[0].value > MAX_HUNDRED;

          if (betweenMaxAngleMaxHundred) {
            data[0].value = MAX_ANGLE_VALUE;
          } else if (greaterThanMaxHundred) {
            data[0].value = MAX_HUNDRED + 0.5;
          }

          // Case where value is less than min value
          const betweenMinAngleMin = data[0].value >= MIN && data[0].value <= MIN_ANGLE_VALUE;
          const lesserThanMin = data[0].value < MIN;

          if (betweenMinAngleMin) {
            data[0].value = MIN_ANGLE_VALUE;
          } else if (lesserThanMin) {
            data[0].value = MIN - 0.5;
          }
        }
        if (acPanel) {
          const MAX_ANGLE_VALUE_AC = MAX_ANGLE_VALUE / 10;
          const MIN_ANGLE_VALUE_AC = MIN_ANGLE_VALUE / 10;

          // Cases where value is greater than max value
          const betweenMaxAngleMaxTen = data[0].value >= MAX_ANGLE_VALUE_AC && data[0].value <= MAX_TEN;
          const greaterThanMaxTen = data[0].value > MAX_TEN;

          if (betweenMaxAngleMaxTen) {
            data[0].value = MAX_ANGLE_VALUE_AC;
          } else if (greaterThanMaxTen) {
            data[0].value = MAX_TEN + 0.05;
          }

          // Case where value is less than min value
          const betweenMinAngleMin = data[0].value >= MIN && data[0].value <= MIN_ANGLE_VALUE_AC;
          const lesserThanMin = data[0].value < MIN;

          if (betweenMinAngleMin) {
            data[0].value = MIN_ANGLE_VALUE_AC;
          } else if (lesserThanMin) {
            data[0].value = MIN - 0.05;
          }
        }

        const theta = 180 - 180 * ((data[0].value - data[0].gauge.axis.range[0]) / (data[0].gauge.axis.range[1] - data[0].gauge.axis.range[0]));
        const r = 0.69; // arrow length
        const x_head = r * Math.cos((Math.PI / 180) * theta); // in rad
        const y_head = r * Math.sin((Math.PI / 180) * theta);

        const layout = {
          width: 240,
          height: 125,
          margin: { t: 20, b: 20, l: 40, r: 40 },
          xaxis: { showgrid: false, zeroline: false, visible: false, range: [-1, 1], fixedrange: true },
          yaxis: { showgrid: false, zeroline: false, visible: false, range: [0, 1], fixedrange: true },
          showlegend: false,
          ...(value != null &&
            value != '-1' && {
              annotations: [
                {
                  ax: 0,
                  ay: 0.15,
                  axref: 'x',
                  ayref: 'y',
                  x: x_head,
                  y: y_head,
                  xref: 'x',
                  yref: 'y',
                  showarrow: true,
                  arrowcolor: 'var(--site-font-secondary-color)',
                  arrowwidth: 2,
                  arrowhead: 2,
                },
              ],
            }),
        };

        const chartWithIndex = 'chart' + this.chartIndex;
        const plotlyPlot = this.$refs[chartWithIndex];

        if (plotlyPlot) {
          // eslint-disable-next-line no-undef
          Plotly.newPlot(plotlyPlot, data, layout, { displayModeBar: false });
          plotlyPlot.style.transform = 'scale(1.4)'; // scale to make it bigger (OC-1784)

          this.createBenchmarkHover(plotlyPlot, benchmarkExists, benchmark);
        }
        this.chartIndex = this.chartIndex += 1;
      }
      this.pieChartsLoading = false;
    },

    createBenchmarkHover(plotlyPlot, benchmarkExists, benchmark) {
      // Industry Benchmark Tooltip
      const prefix = this.chartIndex == 1 ? '$' : '';
      const postfix = this.chartIndex == 2 ? '%' : '';
      let hoverElement = document.querySelector('#' + 'hover-bar' + this.chartIndex);
      if (hoverElement) {
        hoverElement.remove();
      }
      if (benchmarkExists) {
        const element = document.createElement('div');
        const elementChild = document.createElement('div');

        let rate = benchmark / 100;
        if (prefix == '$') {
          rate = 1 - benchmark / 10;
        }
        let topDifference = -2 * rate;
        elementChild.setAttribute('style', 'position: relative; top: -1px; left:' + topDifference + 'px; background-color:transparent');
        elementChild.classList.add('benchmark-triangle');
        elementChild.addEventListener('mouseover', (e) => {
          let tooltipsEl = null;
          if ((benchmark > 10 && benchmark < 90) || (prefix == '$' && benchmark > 1 && benchmark < 9)) {
            tooltipsEl = document.querySelector('.benchmark-tooltips');
            tooltipsEl.style.top = `${e.target.getBoundingClientRect().top - 35}px`;
          } else {
            tooltipsEl = document.querySelector('.site-tooltips');
            tooltipsEl.style.top = `${e.target.getBoundingClientRect().top + 35}px`;
          }

          if (tooltipsEl) {
            if(prefix == '$') {
              tooltipsEl.innerHTML = `Industry Benchmark: ${prefix}${benchmark.toFixed(2)}${postfix}`;
            }
            else {
              tooltipsEl.innerHTML = `Industry Benchmark: ${prefix}${benchmark}${postfix}`;
            }
            tooltipsEl.style.display = 'block';
            tooltipsEl.style.border = '0px';
            tooltipsEl.style.left = `${e.pageX - tooltipsEl.getBoundingClientRect().width / 2}px`;
            if (tooltipsEl.classList.contains('arrow-down')) {
              tooltipsEl.classList.remove('arrow-down');
            }
          }
        });
        elementChild.addEventListener('mouseout', () => {
          let tooltipsEl = null;
          if ((benchmark > 10 && benchmark < 90) || (prefix == '$' && benchmark > 1 && benchmark < 9)) {
            tooltipsEl = document.querySelector('.benchmark-tooltips');
          } else {
            tooltipsEl = document.querySelector('.site-tooltips');
          }
          tooltipsEl.style.display = 'none';
        });
        element.appendChild(elementChild);

        element.setAttribute('id', 'hover-bar' + this.chartIndex);
        if (this.chartIndex == 1) {
          element.setAttribute('style', 'transform: rotateZ(' + (180 - (benchmark / 10) * 180) + 'deg)');
        } else {
          element.setAttribute('style', 'transform: rotateZ(' + (benchmark / 100) * 180 + 'deg)');
        }
        element.classList.add('hover-bar');
        plotlyPlot.appendChild(element);
      }
    },

    async handleFilterChange(disableFetch = false) {
      this.chartIndex = 0; // Important to reset chart index
      this.pieChartsLoading = true;

      if (!disableFetch) {
        // enableFetch: Base condition where we fetch APIs
        this.gaugeData = await this.fetchAutoQASummaryPanel(); // dials panel
        if(this.gaugeData && this.gaugeData.avgAutoEvalScore && this.gaugeData.avgAutoEvalScore.value > 0) {
          this.gaugeData.avgAutoEvalScore.value = this.gaugeData.avgAutoEvalScore.value.toFixed(2);
        }
        const showMainPage = this.checkIfShowMainPage();
        if (!showMainPage) return;
        if(document.querySelector(".gauge-panel .el-loading-mask")) {
          document.querySelector(".gauge-panel .el-loading-mask").style.display = 'none'
        }
        setTimeout(() => {
          this.renderChart();
        }, 500);

        const dynamicChartRef = this.controller.$refs.dynamicChart;

        if (dynamicChartRef) dynamicChartRef.resetNetworkData();
        await this.handlePanelChange('autoEvalScore', true);
      } else {
        // True when called from wy-analytics-configuration:handleFilterChange(true) UI Configuration
        const showMainPage = this.checkIfShowMainPage();
        if (!showMainPage) return;

        setTimeout(() => {
          this.renderChart();
        }, 500);

        await this.handlePanelChange('autoEvalScore');
      }
    },

    checkIfShowMainPage() {
      // Check Gauge Panel all NA
      const gaugeDataKeys = this.gaugeData && Object.keys(this.gaugeData).filter((key) => key !== 'dataFrom' && key !== 'rawData');
      const allGaugeDataIsNA = gaugeDataKeys && Object.keys(gaugeDataKeys).every((key) => this.gaugeData[gaugeDataKeys[key]].value == -1);

      if (allGaugeDataIsNA) {
        this.controller.showMainPage = false;
        return false;
      } else {
        this.controller.showMainPage = true;
        return true;
      }
    },
  },

  mounted() {
    // this.$refs[this.controller.selectedGaugePanel].classList && this.$refs[this.controller.selectedGaugePanel].classList.add('panel-hovered');
  },
});
</script>

<style lang="scss">
.wy-auto-qa.gauge-panel {
  .el-loading-mask {
    max-height: 100% !important;
    border: 1px solid #cad2da;
  }

  border-radius: 5px;
  margin-bottom: 20px;
  background-color: #ffffff;

  display: flex;
  flex-direction: column;

  .panel-section {
    display: flex;
    gap: 22px;
    width: 100%;

    width: 100%;
    flex-wrap: wrap;

    

    .panel-hovered.loading {
      border: unset !important;
      box-shadow: unset;
    }

    .panel-column.gauge {
      border: 1px solid #cad2da;
    }
    .panel-column:hover {
      box-shadow: none !important;
    }
    .panel-column.gauge.loading {
      border: unset !important;
      box-shadow: unset;
    }
    .panel-column.total-overview {
      gap: 24px;
      .panel-row.loading {
        border: unset !important;
        box-shadow: unset;
      }
      .panel-title {
        margin-bottom: 20px;
      }
    }

    .panel-column {
      cursor: default !important;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;

      min-width: 244px;
      min-height: 400px;

      border-radius: 5px;
      // padding: 32px;

      flex: 1 1 0;
      width: 0px;

      .panel-title {
        // font-size: 22px;
        font-size: 22.88px;
        color: var(--site-font-chart-color);
        font-weight: 400;
        // margin-bottom: 20px;
        margin-bottom: 40px;
        margin-top: 39px;
      }

      .panel-info {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        font-weight: 400;
        z-index: 999;

        .min-max-label {
          // display: flex;
          display: none;
          margin-top: -22px;
          font-size: 18px;
          color: #4b5158;
          // z-index: 999;
          width: 407px;
          align-items: center;
          justify-content: space-around;
        }

        .panel-gauge-name {
          margin-top: -28px;
          font-size: 20px;
          font-weight: 400;
          // z-index: 999;
          color: var(--site-font-chart-color);
        }

        .panel-reference {
          font-size: 32px;
        }

        .panel-target {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: -6px;

          span {
            font-size: 14px;
            font-weight: 600;
          }
        }
        .panel-target-svg {
          background-image: url('../../../../assets/icons/icon_target_black.svg');
          width: 14px;
          height: 14px;
          background-repeat: no-repeat;
          background-position: 0px 0px;
          background-size: 100%;
          position: relative;
        }
      }

      .panel-volume-title {
        font-size: 22px;
        color: var(--site-font-chart-color);
        font-weight: 400;
      }

      .panel-volume-value {
        height: 100%;
        font-size: 40px;
        font-weight: 400;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: var(--site-font-chart-color);
      }

      .panel-volume-info {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;

        .volume-pill {
          font-size: 16px;
          display: flex;
          padding: 5px 10px;
          justify-content: center;
          align-items: center;
          gap: 5px;
          border-radius: 100px;

          .volume-up-icon {
            background-image: url('../../../../assets/icons/icon_performance_arrow_up.svg');
            width: 12px;
            height: 12px;
          }

          .volume-down-icon {
            background-image: url('../../../../assets/icons/icon_performance_arrow_down.svg');
            width: 14px;
            height: 14px;
          }
        }

        .volume-details {
          line-height: 1.4;
        }

        .up {
          background-color: #e6e7f8;
          color: #6446bb;
        }

        .down {
          background-color: #effcff;
          color: #3961ae;
        }

        .none {
          background-color: #e4e8ec;
          color: var(--site-font-tertiary-color);
        }
      }
      .hover-bar {
        width: 140px;
        height: 5px;
        left: 39px;
        top: 92px;
        background-color: transparent;
        border: 0px solid black;
        position: absolute;
      }
      .benchmark-triangle {
        width: 24px;
        height: 5px;
        background-image: url('../../../../assets/icons/benchmark_triangle.svg');
        background-size: 100%;
        background-repeat: no-repeat;
      }
      .panel-row {
        border: 1px solid #cad2da;
        border-radius: 5px;
        -ms-flex: 1 1 0px;
        flex: 1 1 0;
        width: calc(100% - 2px);
        display: block;
        .panel-title {
          text-align: center;
        }
        .panel-value {
          text-align: center;
          font-size: 38px;
          font-weight: 400;
        }
      }
      .js-plotly-plot .plotly .cursor-pointer {
        cursor: default;
      }
    }
  }
  .panel-section > * {
    // margin-top: 32px;
  }

  .gauge-benchmark-tooltip {
    position: absolute;
    top: 55%;
    left: 50%;
    transform: translate(-50%, -50%);

    font-weight: 400;
    color: white;
    font-size: 10px;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;

    padding: 4px 6px;
    background-color: #000000;
    border-radius: 4px;

    width: max-content;
  }
}

// create media query for panel-section (if width is smaller than 1640px) into 2 rows
@media (max-width: 1640px) {
  .wy-auto-qa.gauge-panel {
    .panel-section {
      margin-top: 4px;
      justify-content: space-between;
      display: flex;
      flex-wrap: wrap;

      .panel-column {
        margin-top: 0px;
      }
    }
  }
}

.icon-gauge {
  background-size: 100%;
  width: 18px;
  height: 18px;
  background-repeat: no-repeat;
  background-position: 0px 0px;
}
.icon-bes-bas-warning {
  background-image: url('../../../../assets/icons/icon_bes_bas_warning.png');
}

.icon-bes-bas-success {
  background-image: url('../../../../assets/icons/icon_bes_bas_success.png');
}
</style>
